import { useMemo, useState } from "react";
import { PHASE } from "../../../../common/constants.ts";
import type { Phase, View } from "../../../../common/types.ts";
import type { FreeAgentTransaction } from "../../../../common/types/views.ts";
import { range } from "../../../../common/utils.ts";
import { EmptyStatePanel } from "../components/StatePanel.tsx";
import { Mood } from "../components/Mood.tsx";
import { NegotiationModal, useNegotiaionModal } from "../components/NegotiationModal.tsx";
import { NegotiateButtons } from "../components/NegotiateButtons.tsx";
import { PlayerCardList } from "../components/PlayerCardList.tsx";
import { PlayerFaceThumb } from "../components/PlayerFaceThumb.tsx";
import { getRosterPositionNeeds } from "../components/RosterComposition.tsx";
import { TeamAbbrevText } from "../components/TeamAbbrevLink.tsx";
import type { DropdownOption } from "../hooks/useDropdownOptions.tsx";
import {
  MobileActionableEmptyState,
  MobileContentHero,
  MobileSection,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { isCountHighlightRenderable } from "../mobile/statMeaningfulness.ts";
import { helpers } from "../util/helpers.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import i18n from "../util/i18n.ts";
import { useLocalPartial } from "../util/local.ts";
import { createMobileReturnUrl } from "../util/mobilePageContext.ts";
import {
  getPhaseRoute,
  isFreeAgencyAvailable,
} from "../util/phaseNavigation.ts";
import { PHASE_TEXT } from "../util/phaseText.ts";
import { MarketRatingStatusMetric } from "./MarketRatingStatusMetric.tsx";
import {
  type AgeFilter,
  type PriceFilter,
  type SortKey,
  freeAgentRecommendationLimit,
  getActiveFreeAgentFilterCount,
  getEffectiveFreeAgentSortKey,
  getFilteredFreeAgents,
  getFreeAgentRecommendationBuckets,
} from "./freeAgencyTasking.ts";

type FreeAgentPlayer = View<"freeAgents">["players"][number];

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const useSeasonsFreeAgents = () => {
  const { phase, season, startingSeason } = useLocalPartial(["phase", "season", "startingSeason"]);

  const minFreeAgencySeason = startingSeason - 1;
  const maxFreeAgencySeason = phase >= PHASE.PLAYOFFS ? season - 1 : season - 2;

  const options: DropdownOption[] = range(minFreeAgencySeason, maxFreeAgencySeason + 1).map(
    (freeAgencySeason) => {
      let value;
      if (freeAgencySeason >= -10 && freeAgencySeason < 10) {
        value = `${freeAgencySeason}-${freeAgencySeason + 1}`;
      } else {
        value = `${freeAgencySeason}-${String((freeAgencySeason + 1) % 100).padStart(2)}`;
      }

      return {
        key: freeAgencySeason,
        value,
      };
    },
  );

  options.push({
    key: "current",
    value: i18n.t("freeAgents.current", {
      ns: "ui",
      defaultValue: "Current",
    }),
  });

  options.reverse();

  return options;
};

const getRosterSeason = (freeAgencySeason: number, season: number | "current", phase: Phase) => {
  if (season === "current" && phase >= PHASE.PLAYOFFS) {
    return freeAgencySeason;
  }

  return freeAgencySeason + 1;
};

const getUnavailableFreeAgentsPrimaryAction = (phase: Phase) => {
  if (phase === PHASE.RESIGN_PLAYERS) {
    return {
      label: i18n.t("freeAgents.goToNegotiations", {
        ns: "ui",
        defaultValue: "Go to negotiations",
      }),
      path: getPhaseRoute(phase),
    };
  }

  if (phase === PHASE.PLAYOFFS) {
    return {
      label: i18n.t("freeAgents.goToPlayoffs", {
        ns: "ui",
        defaultValue: "Go to playoffs",
      }),
      path: getPhaseRoute(phase),
    };
  }

  if (phase >= PHASE.DRAFT_LOTTERY || phase < PHASE.PRESEASON) {
    return {
      label: i18n.t("freeAgents.goToDraft", {
        ns: "ui",
        defaultValue: "Go to draft",
      }),
      path: getPhaseRoute(phase),
    };
  }

  return {
    label: i18n.t("freeAgents.goToCurrentPhase", {
      ns: "ui",
      defaultValue: "Go to current phase",
    }),
    path: getPhaseRoute(phase),
  };
};

const FilterChoiceGroup = <Value extends string>({
  dataKind,
  label,
  onChange,
  options,
  value,
}: {
  dataKind: string;
  label: string;
  onChange: (value: Value) => void;
  options: readonly {
    label: string;
    value: Value;
  }[];
  value: Value;
}) => (
  <div className="p1v4-filter-choice-group" data-free-agents-filter-choice={dataKind}>
    <div className="form-label small mb-1">{label}</div>
    <div className="d-flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          aria-pressed={value === option.value}
          className={`btn btn-sm ${
            value === option.value ? "btn-primary" : "btn-outline-secondary"
          }`}
          data-free-agents-filter-option={option.value}
          key={option.value}
          onClick={() => onChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const FreeAgents = ({
  capSpace,
  challengeNoFreeAgents,
  challengeNoRatings,
  freeAgencySeason,
  minContract,
  numRosterSpots,
  spectator,
  phase,
  players,
  salaryCapType,
  season,
  type,
  userPlayers,
}: View<"freeAgents">) => {
  const seasonsFreeAgents = useSeasonsFreeAgents();
  const { gameSimInProgress, season: currentSeason } = useLocalPartial([
    "gameSimInProgress",
    "season",
  ]);
  const negotiationModal = useNegotiaionModal();
  const [positionFilter, setPositionFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState<AgeFilter>("all");
  const [upsideOnly, setUpsideOnly] = useState(false);
  const [priceFilter, setPriceFilter] = useState<PriceFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("ovr");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const minContractAmount = minContract / 1000;
  const filters = {
    ageFilter,
    positionFilter,
    priceFilter,
    sortKey,
    upsideOnly,
  };
  const activeFilterCount = getActiveFreeAgentFilterCount(filters);
  const effectiveSortKey = getEffectiveFreeAgentSortKey({
    challengeNoRatings,
    sortKey,
  });
  const rosterNeeds = getRosterPositionNeeds(userPlayers);
  const rosterNeedPositions = new Set(rosterNeeds.map(({ pos }) => pos));
  const marketHomeHref = helpers.leagueUrl(["market"]);
  const isSignedHistory = type === "signed" && season !== "current";
  const signedHistorySeason = freeAgencySeason;
  const signedHistoryClearHref = helpers.leagueUrl([
    "free_agents",
    "both",
    signedHistorySeason,
  ]);
  const signedHistoryCurrentMarketHref = helpers.leagueUrl(
    isFreeAgencyAvailable(phase) ? ["free_agents"] : ["market"],
  );
  const resetFilters = () => {
    setPositionFilter("all");
    setAgeFilter("all");
    setPriceFilter("all");
    setSortKey("ovr");
    setUpsideOnly(false);
  };

  useTitleBar({
    title: isSignedHistory
      ? i18n.t("freeAgents.signedHistoryTitle", {
          ns: "ui",
          season: signedHistorySeason,
          defaultValue: "{{season}} signing history",
        })
      : i18n.t("freeAgents.title", {
          ns: "ui",
          defaultValue: "Free Agents",
        }),
    dropdownView: "free_agents",
    dropdownFields: { typeFreeAgents: type, seasonsFreeAgents: season },
    dropdownCustomOptions: {
      seasonsFreeAgents,
    },
  });

  const canAfford = (p: FreeAgentPlayer) => {
    if (p.freeAgentType !== "available") {
      return false;
    }
    if (salaryCapType === "none") {
      return true;
    }
    if (challengeNoFreeAgents) {
      return p.contract.amount <= minContractAmount;
    }
    return p.contract.amount <= Math.max(minContractAmount, capSpace);
  };

  const positions = useMemo(
    () => ["all", ...Array.from(new Set(players.map((p) => p.ratings.pos))).sort()],
    [players],
  );
  const positionOptions = positions.map((position) => ({
    label: position === "all" ? i18n.t("common.all", { ns: "ui", defaultValue: "All" }) : position,
    value: position,
  }));
  const ageOptions = [
    {
      label: i18n.t("common.all", { ns: "ui", defaultValue: "All" }),
      value: "all",
    },
    {
      label: i18n.t("freeAgents.ageYoung", {
        ns: "ui",
        defaultValue: "24 and under",
      }),
      value: "young",
    },
    {
      label: i18n.t("freeAgents.agePrime", {
        ns: "ui",
        defaultValue: "25-30",
      }),
      value: "prime",
    },
    {
      label: i18n.t("freeAgents.ageVeteran", {
        ns: "ui",
        defaultValue: "31+",
      }),
      value: "veteran",
    },
  ] as const satisfies readonly { label: string; value: AgeFilter }[];
  const priceOptions = [
    {
      label: i18n.t("common.all", { ns: "ui", defaultValue: "All" }),
      value: "all",
    },
    {
      label: i18n.t("freeAgents.affordable", {
        ns: "ui",
        defaultValue: "Affordable",
      }),
      value: "affordable",
    },
    {
      label: i18n.t("freeAgents.minimum", {
        ns: "ui",
        defaultValue: "Minimum",
      }),
      value: "minimum",
    },
  ] as const satisfies readonly { label: string; value: PriceFilter }[];
  const sortOptions = [
    ...(!challengeNoRatings
      ? [
          {
            label: i18n.t("title.Ovr", { ns: "cols", defaultValue: "Overall" }),
            value: "ovr" as const,
          },
          {
            label: i18n.t("title.Pot", { ns: "cols", defaultValue: "Potential" }),
            value: "pot" as const,
          },
        ]
      : []),
    {
      label: i18n.t("freeAgents.asking", {
        ns: "ui",
        defaultValue: "Asking",
      }),
      value: "asking" as const,
    },
  ] as const satisfies readonly { label: string; value: SortKey }[];

  if (
    ((phase > PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.RESIGN_PLAYERS) ||
      phase === PHASE.FANTASY_DRAFT ||
      phase === PHASE.EXPANSION_DRAFT) &&
    season === "current"
  ) {
    const primaryAction = getUnavailableFreeAgentsPrimaryAction(phase);

    // B2b (v5Content): the closed state is one sentence + one CTA — the A3
    // MobileActionableEmptyState (reason + impact + single action). The
    // legacy management-template narration wrapper and the "market prep" grid
    // repeating the same unavailableBody line are gone (法则一/法则二).
    return (
      <div
        data-free-agents-closed-phase={phase}
        data-free-agents-closed-state=""
        data-v5-free-agents-closed-first-screen=""
      >
        <div
        >
          {/* A3: reason + impact + a single recommended action (no flat button stacks) */}
          <MobileActionableEmptyState
            action={
              <a className="btn btn-primary" href={helpers.leagueUrl(primaryAction.path)}>
                {primaryAction.label}
              </a>
            }
            data-a3-free-agents-closed-empty-state=""
            reason="unavailable"
            reasonText={i18n.t("freeAgents.unavailableBody", {
              ns: "ui",
              phase: PHASE_TEXT[phase],
              defaultValue:
                "You can sign free agents before the playoffs or after re-signing players. Current phase: {{phase}}.",
            })}
            impact={i18n.t("freeAgents.unavailableImpact", {
              ns: "ui",
              defaultValue:
                "No signings are possible right now; your cap space and roster stay unchanged until the market reopens.",
            })}
            title={i18n.t("freeAgents.unavailableTitle", {
              ns: "ui",
              defaultValue: "Free agents are closed right now",
            })}
          />
        </div>
        <div
          className="mt-3"
        >
          {/* A4-aligned role: text-level return link, not a competing CTA */}
          <a className="btn btn-link btn-sm px-0" href={marketHomeHref}>
            {i18n.t("freeAgents.returnToMarket", {
              ns: "ui",
              defaultValue: "Market home",
            })}
          </a>
        </div>
      </div>
    );
  }

  const playerInfoSeason =
    freeAgencySeason + (season === "current" && phase < PHASE.FREE_AGENCY ? 1 : 0);

  const filteredPlayers = getFilteredFreeAgents({
    canAfford,
    challengeNoRatings,
    filters,
    minContractAmount,
    players,
  });
  const signedHistoryPlayers = filteredPlayers.filter((p) => p.freeAgentType === "signed");
  const signedHistoryTeamCount = new Set(
    signedHistoryPlayers.map((p) => p.freeAgentTransaction?.tid).filter((tid) => tid !== undefined),
  ).size;
  const signedHistoryTotalAmount = signedHistoryPlayers.reduce(
    (sum, p) => sum + p.contract.amount,
    0,
  );
  const recommendationBuckets = getFreeAgentRecommendationBuckets({
    canAfford,
    challengeNoRatings,
    players: filteredPlayers,
    rosterNeedPositions,
  });
  const filterPanelClassName = filterPanelOpen
    ? "d-flex flex-wrap align-items-end gap-2 mb-3"
    : "d-none d-md-flex flex-wrap align-items-end gap-2 mb-3";
  const createMarketPlayerHref = (pid: number) =>
    createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
      activeTab: isSignedHistory ? "signedHistory" : effectiveSortKey,
      filterKey: "freeAgentFilters",
      filterValue: isSignedHistory
        ? `signed:${signedHistorySeason}|${positionFilter}|${ageFilter}|${priceFilter}|${upsideOnly}`
        : `${positionFilter}|${ageFilter}|${priceFilter}|${upsideOnly}`,
      pageID: "freeAgents",
      returnLabel: i18n.t("freeAgents.returnToMarket", {
        ns: "ui",
        defaultValue: "Market home",
      }),
      scrollY: getMobileScrollY(),
    });

  const renderPlayerList = (
    playersToRender: FreeAgentPlayer[],
    { withFaces = false }: { withFaces?: boolean } = {},
  ) => (
    <PlayerCardList
      players={playersToRender}
      season={playerInfoSeason}
      shouldShowRatings={() => false}
      getJerseyNumber={(p) => p.jerseyNumber}
      getContract={(p) => p.contract}
      getPlayerHref={(p) => createMarketPlayerHref(p.pid)}
      renderMedia={
        // C2: face thumbnails on the recommendation hero rows only (lazy +
        // cached inside PlayerFaceThumb).
        withFaces
          ? (p) => <PlayerFaceThumb face={p.face} imgURL={p.imgURL} pid={p.pid} size={44} />
          : undefined
      }
      renderMetadata={(p) => {
        const signedHistory = isSignedHistory && p.freeAgentType === "signed";
        if (challengeNoRatings && !signedHistory) {
          return null;
        }

        const tx = signedHistory
          ? (p.freeAgentTransaction as FreeAgentTransaction & {
              abbrev: string;
            })
          : undefined;
        const rosterSeason = getRosterSeason(freeAgencySeason, season, phase);

        return (
          <>
            {!challengeNoRatings ? (
              <MarketRatingStatusMetric
                label={i18n.t("marketMetrics.playerLevel", {
                  ns: "ui",
                  defaultValue: "Player level",
                })}
                potential={p.ratings.pot}
                rating={p.ratings.ovr}
              />
            ) : null}
            {tx ? (
              <div className="d-flex flex-wrap gap-2 small text-body-secondary mt-2">
                <span>
                  {i18n.t("freeAgents.signedHistoryContract", {
                    ns: "ui",
                    amount: helpers.formatCurrency(p.contract.amount, "M"),
                    exp: p.contract.exp,
                    defaultValue: "Contract: {{amount}} through {{exp}}",
                  })}
                </span>
                <span>
                  {i18n.t("freeAgents.signedHistoryDate", {
                    ns: "ui",
                    phase: PHASE_TEXT[tx.phase],
                    season: tx.season,
                    defaultValue: "Signed: {{season}} · {{phase}}",
                  })}
                </span>
                <span>
                  {i18n.t("freeAgents.signedHistoryOrigin", {
                    ns: "ui",
                    defaultValue: "Team:",
                  })}{" "}
                  <a
                    href={createMobileCrossTabHref(
                      helpers.leagueUrl(["roster", `${tx.abbrev}_${tx.tid}`, rosterSeason]),
                    )}
                  >
                    <TeamAbbrevText tid={tx.tid} abbrev={tx.abbrev} />
                  </a>
                </span>
              </div>
            ) : null}
          </>
        );
      }}
      getRole={(p) =>
        p.freeAgentType !== "available"
          ? i18n.t("freeAgents.signed", {
              ns: "ui",
              defaultValue: "Signed",
            })
          : canAfford(p)
            ? i18n.t("freeAgents.affordable", {
                ns: "ui",
                defaultValue: "Affordable",
              })
            : i18n.t("freeAgents.capLimited", {
                ns: "ui",
                defaultValue: "Cap limited",
              })
      }
      getReasonTags={(p) => {
        const tags = [];
        if (p.freeAgentType !== "available") {
          tags.push(
            i18n.t("freeAgents.signed", {
              ns: "ui",
              defaultValue: "Signed",
            }),
          );
        } else if (p.mood.user.willing) {
          tags.push(
            i18n.t("freeAgents.willing", {
              ns: "ui",
              defaultValue: "Willing",
            }),
          );
        } else {
          tags.push(
            i18n.t("freeAgents.refuses", {
              ns: "ui",
              defaultValue: "Refuses!",
            }),
          );
        }
        if (rosterNeedPositions.has(p.ratings.pos)) {
          tags.push(
            i18n.t("freeAgents.rosterNeed", {
              ns: "ui",
              defaultValue: "Roster need",
            }),
          );
        }
        if (!challengeNoRatings && p.ratings.pot - p.ratings.ovr >= 10) {
          tags.push(
            i18n.t("playerCard.tagHighUpside", {
              ns: "ui",
              defaultValue: "High upside",
            }),
          );
        }
        if (p.contract.amount <= minContractAmount) {
          tags.push(
            i18n.t("freeAgents.minimum", {
              ns: "ui",
              defaultValue: "Minimum",
            }),
          );
        }
        return tags.slice(0, 3);
      }}
      renderMood={(p) =>
        p.freeAgentType === "available" ? (
          <Mood
            defaultType="user"
            host="free-agents"
            maxWidth
            p={p}
            playerHref={createMarketPlayerHref(p.pid)}
          />
        ) : null
      }
      renderActions={(p) => {
        if (p.freeAgentType === "available") {
          return (
            <div
              data-offer-return-state=""
              data-player-affordable={canAfford(p)}
              data-player-willing={p.mood.user.willing}
            >
              <NegotiateButtons
                canGoOverCap={salaryCapType === "none"}
                capSpace={capSpace}
                disabled={gameSimInProgress}
                minContract={minContract}
                onNegotiate={async () => {
                  await negotiationModal.negotiate(p.pid);
                }}
                season={currentSeason}
                spectator={spectator}
                p={p}
                willingToNegotiate={p.mood.user.willing}
              />
            </div>
          );
        }
        if (isSignedHistory) {
          return null;
        }

        const tx = p.freeAgentTransaction as FreeAgentTransaction & {
          abbrev: string;
        };
        return (
          <span className="small text-body-secondary">
            <a
              href={helpers.leagueUrl([
                "roster",
                `${tx.abbrev}_${tx.tid}`,
                getRosterSeason(freeAgencySeason, season, phase),
              ])}
            >
              <TeamAbbrevText tid={tx.tid} abbrev={tx.abbrev} />
            </a>
            , {PHASE_TEXT[tx.phase]}
          </span>
        );
      }}
    />
  );
  const emptyStateAction = isSignedHistory ? (
    <a
      className="btn btn-outline-secondary btn-sm"
      href={signedHistoryCurrentMarketHref}
    >
      {i18n.t("freeAgents.signedHistoryCurrentMarket", {
        ns: "ui",
        defaultValue: "Current market",
      })}
    </a>
  ) : activeFilterCount > 0 ? (
    <button className="btn btn-outline-secondary btn-sm" type="button" onClick={resetFilters}>
      {i18n.t("common.reset", {
        ns: "ui",
        defaultValue: "Reset",
      })}
    </button>
  ) : null;

  // B2b (v5Content): the open-state first screen IS the recommended signings
  // list (falling back to the full match list when no recommendation clears
  // the affordability/willingness bar); the "market summary" narration hero,
  // the RosterComposition card, and the salary summary sentence are gone —
  // salary impact and roster gap survive as the single hero explainer line.
  const recommendedPlayers = recommendationBuckets.recommended;
  const isCurrentMarket = !isSignedHistory && season === "current";
  const heroPlayers =
    recommendedPlayers.length > 0 ? recommendedPlayers : filteredPlayers;
  const remainingPlayers = isCurrentMarket
    ? recommendedPlayers.length > 0
      ? recommendationBuckets.remaining
      : []
    : filteredPlayers;
  const marketExplainer = (
    <>
      <span>
        {i18n.t("freeAgents.explainerCapSpace", {
          ns: "ui",
          value: helpers.formatCurrency(capSpace, "M"),
          defaultValue: "Cap space {{value}}",
        })}
      </span>
      {" · "}
      <span>
        {i18n.t("freeAgents.explainerRosterGap", {
          ns: "ui",
          needs: rosterNeeds.length,
          spots: numRosterSpots,
          defaultValue: "{{needs}} roster need(s) · {{spots}} open spot(s)",
        })}
      </span>
    </>
  );

  return (
    <div
      data-host-state={filteredPlayers.length === 0 ? "empty" : "ready"}
      data-host-state-host="freeAgents"
      data-host-state-row-count={filteredPlayers.length}
      data-v5-free-agents-content-first-screen=""
    >
      <div data-free-agents-market-body="">
        {gameSimInProgress && !spectator ? (
          <p className="text-danger">
            {i18n.t("freeAgents.stopSim", {
              ns: "ui",
              defaultValue: "Stop game simulation to sign free agents.",
            })}
          </p>
        ) : null}

        {spectator ? (
          <div>
            <div className="alert alert-danger d-inline-block">
              {i18n.t("freeAgents.spectatorMode", {
                ns: "ui",
                defaultValue: "The AI will handle signing free agents in spectator mode.",
              })}
            </div>
          </div>
        ) : challengeNoFreeAgents ? (
          <div>
            <div className="alert alert-danger d-inline-block">
              <b>
                {i18n.t("common.challengeMode", {
                  ns: "ui",
                  defaultValue: "Challenge Mode:",
                })}
              </b>{" "}
              {i18n.t("freeAgents.challengeNoFreeAgents", {
                ns: "ui",
                defaultValue:
                  "You are not allowed to sign free agents, except to minimum contracts.",
              })}
            </div>
          </div>
        ) : null}
        {isSignedHistory && isCountHighlightRenderable(signedHistoryPlayers.length) ? (
          // B2b (v5Content): the archive hero narration (eyebrow / description)
          // and the filter-state chip row are gone; the first screen is the
          // summary numbers as stat rows plus the two archive actions. A3:
          // an all-zero summary would duplicate the empty state, so it only
          // renders when records exist.
          <MobileContentHero
            action={
              <div
                className="d-flex flex-wrap gap-2"
              >
                <a
                  className="btn btn-outline-secondary btn-sm"
                  href={signedHistoryClearHref}
                >
                  {i18n.t("freeAgents.signedHistoryClearFilter", {
                    ns: "ui",
                    defaultValue: "Clear signed filter",
                  })}
                </a>
                <a
                  className="btn btn-outline-secondary btn-sm"
                  href={signedHistoryCurrentMarketHref}
                >
                  {i18n.t("freeAgents.signedHistoryCurrentMarket", {
                    ns: "ui",
                    defaultValue: "Current market",
                  })}
                </a>
              </div>
            }
            className="mb-3"
            data-v5-signed-history-summary-first-screen=""
          >
            <div
            >
              <MobileStatRow
                label={i18n.t("freeAgents.signedHistoryRecords", {
                  ns: "ui",
                  defaultValue: "Records",
                })}
                value={signedHistoryPlayers.length}
              />
              <MobileStatRow
                label={i18n.t("freeAgents.signedHistoryCommitted", {
                  ns: "ui",
                  defaultValue: "Committed",
                })}
                value={helpers.formatCurrency(signedHistoryTotalAmount, "M")}
              />
              <MobileStatRow
                label={i18n.t("freeAgents.signedHistoryTeams", {
                  ns: "ui",
                  defaultValue: "Teams",
                })}
                value={signedHistoryTeamCount}
              />
            </div>
          </MobileContentHero>
        ) : isCurrentMarket && filteredPlayers.length > 0 ? (
          <MobileContentHero
            className="mb-3"
            data-free-agent-recommendation-model=""
            data-free-agents-recommendations
            data-recommended-count={recommendedPlayers.length}
            data-recommended-limit={freeAgentRecommendationLimit}
            data-v5-free-agents-recommended-first-screen=""
            explainer={marketExplainer}
          >
            {renderPlayerList(heroPlayers, { withFaces: true })}
          </MobileContentHero>
        ) : null}

        <div
          className="d-md-none mb-3"
          data-free-agents-filter-summary
          data-active-filter-count={activeFilterCount}
          data-filtered-player-count={filteredPlayers.length}
        >
          <button
            aria-controls="free-agents-filter-panel"
            aria-expanded={filterPanelOpen}
            className="btn btn-outline-secondary btn-sm w-100 d-flex justify-content-between align-items-center"
            data-free-agents-filter-toggle
            type="button"
            onClick={() => {
              setFilterPanelOpen((value) => !value);
            }}
          >
            <span>
              {i18n.t("freeAgents.filters", {
                ns: "ui",
                defaultValue: "Filters",
              })}
            </span>
            <span className="small text-body-secondary">
              {i18n.t("freeAgents.filterSummary", {
                ns: "ui",
                count: activeFilterCount,
                matches: filteredPlayers.length,
                defaultValue: "{{count}} active · {{matches}} matches",
              })}
            </span>
          </button>
        </div>

        <div
          className={filterPanelClassName}
          data-filter-sheet=""
          data-free-agents-filter-panel
          id="free-agents-filter-panel"
        >
          <FilterChoiceGroup
            dataKind="position"
            label={i18n.t("freeAgents.position", {
              ns: "ui",
              defaultValue: "Position",
            })}
            onChange={setPositionFilter}
            options={positionOptions}
            value={positionFilter}
          />
          <FilterChoiceGroup
            dataKind="age"
            label={i18n.t("freeAgents.age", { ns: "ui", defaultValue: "Age" })}
            onChange={setAgeFilter}
            options={ageOptions}
            value={ageFilter}
          />
          <FilterChoiceGroup
            dataKind="price"
            label={i18n.t("freeAgents.price", { ns: "ui", defaultValue: "Price" })}
            onChange={setPriceFilter}
            options={priceOptions}
            value={priceFilter}
          />
          <FilterChoiceGroup
            dataKind="sort"
            label={i18n.t("freeAgents.sort", { ns: "ui", defaultValue: "Sort" })}
            onChange={setSortKey}
            options={sortOptions}
            value={effectiveSortKey}
          />
          {!challengeNoRatings ? (
            <button
              className={`btn btn-sm ${upsideOnly ? "btn-primary" : "btn-outline-primary"}`}
              type="button"
              onClick={() => setUpsideOnly((value) => !value)}
            >
              {i18n.t("freeAgents.upside", {
                ns: "ui",
                defaultValue: "Upside",
              })}
            </button>
          ) : null}
          {activeFilterCount > 0 ? (
            <button className="btn btn-link btn-sm px-0" type="button" onClick={resetFilters}>
              {i18n.t("common.reset", {
                ns: "ui",
                defaultValue: "Reset",
              })}
            </button>
          ) : null}
        </div>

        {filteredPlayers.length === 0 ? (
          <div
          >
            <EmptyStatePanel
              action={emptyStateAction}
              emptyReason={isSignedHistory || activeFilterCount > 0 ? "filtered" : "empty"}
              title={
                isSignedHistory
                  ? i18n.t("freeAgents.signedHistoryEmptyTitle", {
                      ns: "ui",
                      defaultValue: "No signing records match",
                    })
                  : i18n.t("freeAgents.noMatchesTitle", {
                      ns: "ui",
                      defaultValue: "No free agents match",
                    })
              }
            >
              {isSignedHistory
                ? i18n.t("freeAgents.signedHistoryEmptyBody", {
                    ns: "ui",
                    season: signedHistorySeason,
                    defaultValue:
                      "No signed free agents match the {{season}} history route and current card filters.",
                  })
                : i18n.t("freeAgents.noMatchesBody", {
                    ns: "ui",
                    defaultValue:
                      "Adjust filters or browse all positions to find a signing option.",
                  })}
            </EmptyStatePanel>
          </div>
        ) : remainingPlayers.length > 0 ? (
          // 法则二: the recommended rows already shown in the hero do not
          // repeat here — this section renders the remaining matches only
          // (signed history / archive routes render their full record list).
          <MobileSection
            aria-label={
              isSignedHistory
                ? i18n.t("freeAgents.p2s06TransactionTimeline", {
                    ns: "ui",
                    defaultValue: "Season transaction timeline",
                  })
                : undefined
            }
            data-free-agents-all-matches
            data-filtered-player-count={filteredPlayers.length}
            data-remaining-player-count={remainingPlayers.length}
            title={
              isSignedHistory
                ? i18n.t("freeAgents.signedHistoryRecordsTitle", {
                    ns: "ui",
                    defaultValue: "Signing records",
                  })
                : season === "current"
                  ? i18n.t("freeAgents.remainingMatches", {
                      ns: "ui",
                      defaultValue: "More matches",
                    })
                  : i18n.t("freeAgents.allMatches", {
                      ns: "ui",
                      defaultValue: "All matches",
                    })
            }
          >
            {renderPlayerList(remainingPlayers)}
          </MobileSection>
        ) : null}

        <div
          className="mt-3"
        >
          {/* A4-aligned role: text-level return link, not a competing CTA */}
          <a className="btn btn-link btn-sm px-0" href={marketHomeHref}>
            {i18n.t("freeAgents.returnToMarket", {
              ns: "ui",
              defaultValue: "Market home",
            })}
          </a>
        </div>
      </div>
      <NegotiationModal {...negotiationModal.props} />
    </div>
  );
};

export default FreeAgents;
